import { useEffect, useState } from "react";
import { fetchMaterialContentObjectUrl } from "@/lib/materials-content-cache";
import type { HermesMaterialImagePreview } from "@/lib/hermes-material-preview";
import { cn } from "@/lib/utils";

type HermesMaterialPreviewCardProps = {
  accountId: string;
  preview: HermesMaterialImagePreview;
};

/** 聊天区素材缩略图：经登录态 materials preview 拉取，不展示公网/blob 外链。 */
export function HermesMaterialPreviewCard({
  accountId,
  preview,
}: HermesMaterialPreviewCardProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const title = preview.name?.trim() || preview.slug || "素材预览";
  const notes = preview.notes?.trim() || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setObjectUrl(null);

    async function load() {
      try {
        const url = await fetchMaterialContentObjectUrl(accountId, preview.id, "preview");
        if (cancelled) return;
        setObjectUrl(url);
      } catch {
        if (!cancelled) {
          setLoadError("缩略图加载失败");
          setObjectUrl(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [accountId, preview.id]);

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm",
      )}
    >
      <div className="flex items-start gap-3 px-3 py-2.5">
        <div className="relative size-16 shrink-0 overflow-hidden rounded-md bg-muted">
          {loading ? (
            <div className="flex size-full items-center justify-center text-[10px] text-muted-foreground">
              加载中
            </div>
          ) : loadError || !objectUrl ? (
            <div className="flex size-full items-center justify-center px-1 text-center text-[10px] text-muted-foreground">
              {loadError || "无预览"}
            </div>
          ) : (
            <img
              src={objectUrl}
              alt={title}
              className="size-full object-cover"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {preview.slug}
          </p>
          {notes ? (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{notes}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
